import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkSession = useCallback(() => {
    setChecking(true);
    return fetch(apiUrl('/session.php'), { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        setAuthenticated(!!data.authenticated);
        setUsername(data.username || null);
        return !!data.authenticated;
      })
      .catch(() => {
        setAuthenticated(false);
        setUsername(null);
        return false;
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (user, password) => {
    const res = await fetch(apiUrl('/login.php'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: user, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Login failed.');
    }
    setAuthenticated(true);
    setUsername(data.username || user);
    return true;
  }, []);

  const logout = useCallback(async () => {
    await fetch(apiUrl('/logout.php'), { method: 'POST', credentials: 'include' }).catch(() => {});
    setAuthenticated(false);
    setUsername(null);
  }, []);

  return (
    <AuthContext.Provider value={{ authenticated, username, checking, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
